import { createSelector } from "@reduxjs/toolkit";
import { NaturalPersonDto } from "api/actor";
import { RootState } from "app/store";
import { ActorType } from "features/new-actor/enums/actorType";
import { createLoaderSelector } from "helpers/selectLoader";
import { UnemploymentData, UpiData } from "../types/borrowerData";
import { UPIStepIdentifier } from "../types/enums";
import { InsuredEntity, PendingEntity, SubscriptionProcessStepIdentifier } from "../types/interfaces";
import { SubscriptionProcessState } from "./subscriptionProcessSlice";

export const selectSubscriptionProcessState = (state: RootState): SubscriptionProcessState => {
  return state.subscriptionProcess;
};

export const selectLoadingInitSubscriptionProcess = createLoaderSelector(selectSubscriptionProcessState, 'initSubscriptionProcess');
export const selectLoadingPendingForm = createLoaderSelector(selectSubscriptionProcessState, 'pendingForm');
export const selectLoadingSteps = createLoaderSelector(selectSubscriptionProcessState, 'steps');

export const selectBranchCode = createSelector(
  [
    (state: RootState) => state.subscriptionProcess.branch,
  ],
  (branchCode) => branchCode
);

export const selectStepData = (stepIdentifier: SubscriptionProcessStepIdentifier)=> createSelector(
  selectSubscriptionProcessState,
  (sp) => sp.data && (sp.data as any)[stepIdentifier]
);

export const selectRiskId = createSelector(
  selectSubscriptionProcessState,
  (sp) => sp.riskId
);

export const selectSubscriptionProcessData = createSelector(
  selectSubscriptionProcessState,
  (sp) => sp.data
);

export const selectUpiData = createSelector(
  selectSubscriptionProcessState,
  (sp) => sp.data as UpiData
);

export const selectInsuredId = createSelector(
  selectSubscriptionProcessState,
  (sp) => sp.steps.find(step => step.stepIdentifier === UPIStepIdentifier.Pricing) as UpiData
);

export const selectUnemploymentData = createSelector(
  selectSubscriptionProcessState,
  (sp) => sp.data as UnemploymentData
);

export const selectPendingForm = () => createSelector(
  selectSubscriptionProcessState,
  (sp) => {
    if (!sp.pendingForm || !sp.pendingForm.entity) {
      return {
        entity: null,
        formKey: null,
      };
    }
    return {
      entity: sp.pendingForm.entity,
      formKey: sp.pendingForm.formKey,
    };
  }
);

const isNaturalPerson = (
  entity: PendingEntity
): entity is NaturalPersonDto => {
  return entity !== undefined && 'lastName' in entity;
};

export const selectInsuredPerson = createSelector(
  [(state: RootState) => state.subscriptionProcess.pendingForm?.entity],
  (insuredEntity): InsuredEntity | undefined => {
    if (!insuredEntity) {
      return undefined
    }
    if (isNaturalPerson(insuredEntity)) {
      return {
        type: ActorType.Natural,
        insuredEntity
      };
    }
    return {
      type: ActorType.Legal,
      insuredEntity
    };
  }
);

export const selectSimulationConfig = createSelector([
  (state: RootState) => state.subscriptionProcess.simulationConfig
], (config) => config)

export const selectSubscriptionProcessSteps = createSelector([
  (state: RootState) => state.subscriptionProcess.steps,
  (state: RootState) => state.subscriptionProcess.simulationConfig
], (steps, simulationConfig) => {
  const currentStep = steps.find(step => step.isCurrent)
  if (currentStep && simulationConfig) {
    const step = simulationConfig.steps.find(step => step.stepIdentifiers.includes(currentStep?.stepIdentifier))
    if (step) {
      return {
        stepNumber: step.id,
        currentStep
      }
    }
  }
  return {
    stepNumber: 0,
    currentStep
  }
})
